import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getFoodPartnerAction } from '../../../../../../features/food_partner/get_food_partner';
import { getFoodPartnerNotificationData } from '../../../../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT } from '../../../../../../core/constants';
import ThreeLevelSteps from '../../../../common/steps';
import DestinationsList from '../common/destination_list';
import AdditionalOptions from '../common/additional_options';
import { getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import UploadFiles from '../../../../common/warehouse_and_food_partner_order_form/upload_files';
import TextFieldFormik from '../../../../../common/text_field';
import AddPodDestinationDialog from '../../../../common/warehouse_and_food_partner_order_form/add_pod_destination_dialog';
import useUnsavedChangesWarning from '../../../../../layout/component/use_unsaved_changes_warning';
import SectionPaper from '../../../../../common/section_paper';
import BottomActionBar from '../../../../../common/bottom_action_bar';
import { SubmitButton } from '../../../../../common/button';
import BlockerDialog from '../../../../common/blocker_dialog';
import BlockerReturnButton from '../../../../common/blocker_return_button';
import { useDirtyForm } from '../../../../../../core/context/dirty_form_context';

const FormBody = ({ form, onChangeSetDate, onCopyHistoricalOrder, onReturn }) => {
    const formik = useFormikContext();
    useUnsavedChangesWarning(formik.dirty);
    const { setIsDirty } = useDirtyForm();

    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        formik.dirty && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        setIsDirty(formik.dirty);

        return () => setIsDirty(false);
    }, [formik.dirty, setIsDirty]);

    return (
        <>
            <BlockerDialog blocker={ blocker }/>
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                <Box sx={ { flexGrow: 1, pb: 4 } }>
                    <Stack spacing={ 3 }>
                        <ThreeLevelSteps secondStep/>

                        <AdditionalOptions
                            onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                            displayCopyHistoricalDialog
                            form={ form }
                            onCopyHistoricalOrder={ (mainWarehouseValues, podValues) => onCopyHistoricalOrder(mainWarehouseValues, podValues, formik) }
                            formik={ formik }
                        />

                        <SectionPaper title="Lokalizacje i produkty">
                            <Stack spacing={ 2 }>
                                <DestinationsList
                                    form={ form }
                                    withExpirationDate
                                    formik={ formik }
                                />
                                <AddPodDestinationDialog
                                    formik={ formik }
                                />
                            </Stack>
                        </SectionPaper>

                        <SectionPaper>
                            <Stack spacing={ 3 }>
                                <UploadFiles formik={ formik } addAndRemoveOptions/>
                                <Box>
                                    <Typography variant="subtitle2" sx={ {
                                        fontWeight: 600,
                                        mb: 1,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase'
                                    } }>
                                        Notatka do zamówienia
                                    </Typography>
                                    <TextFieldFormik name="orderNote" multiline rows={ 3 }/>
                                </Box>
                            </Stack>
                        </SectionPaper>
                    </Stack>
                </Box>

                <BottomActionBar>
                    <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                        <BlockerReturnButton formik={ formik } onReturn={ () => onReturn() }/>
                        <SubmitButton type="button" onClick={ formik.submitForm }>Dalej</SubmitButton>
                    </Box>
                </BottomActionBar>
            </Box>
        </>
    );
};

const FormWithLocationDivision = ({ form, setView, data, setData, errors, setErrors }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getAllPods = useSelector(getAllPodsState);

    useEffect(() => {
        dispatch(getFoodPartnerAction(form?.foodPartnerId))
            .then(response => showErrorMessage(getFoodPartnerNotificationData(response)));
    }, [dispatch, form, showErrorMessage]);

    const initialValues = () => {
        const destinationsList = [];
        const mainWarehouseIndex = 0;

        destinationsList.push({
            position: mainWarehouseIndex,
            name: 'Magazyn główny',
            destinationPopId: null,
            destinationPodId: null,
            canBeRemoved: false
        });

        return {
            destinationsList: data?.formik?.destinationsList || destinationsList,
            productsList: data?.formik?.productsList || [],
            attachments: data?.formik?.attachments || [],
            orderNote: data?.formik?.orderNote || '',
        };
    };

    const initialErrors = {
        ...errors
    };

    const onReturn = () => {
        setData({});
        setView(0);
    };

    const onChangeSetDate = (newDate, allDates, formik) => {
        formik?.values?.destinationsList?.forEach((_, index) => {
            const productsList = formik?.values?.productsList[index]?.map(product => {
                if (!allDates && product?.expirationDate) {
                    return product;
                }

                return ({ ...product, expirationDate: newDate });
            });

            if (productsList?.length !== 0) {
                formik.setFieldValue(`productsList.${ index }`, productsList);
            }
        });
    };

    const onCopyHistoricalOrder = (mainWarehouseValues, podValues, formik) => {
        const destinationsList = [];
        const productsList = [];

        if (!getAllPods?.data) return;

        const mainWarehouseIndex = 0;

        destinationsList.push({
            position: mainWarehouseIndex,
            name: 'Magazyn główny',
            destinationPopId: null,
            destinationPodId: null,
            canBeRemoved: false
        });
        productsList[mainWarehouseIndex] = mainWarehouseValues.map((product, productIndex) => ({
            position: productIndex,
            name: product?.productName,
            productId: product?.productId,
            quantity: product?.quantity,
            expirationDate: product?.defaultDaysToExpiration ? moment(form?.orderDate).add(product?.defaultDaysToExpiration - 1, 'days') : '',
            price: product?.purchaseNetPrice ? Number(product.purchaseNetPrice).toFixed(2) : ''
        }));

        Object.keys(podValues)?.forEach((key, index) => {
            destinationsList.push({
                position: index + 1,
                name: getAllPods?.data?.map(pod => ({
                    id: pod?.id,
                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                })).find(pod => pod?.id === key)?.label,
                destinationPodId: key,
                canBeRemoved: true
            });

            productsList[index + 1] = podValues?.[key]?.map((product, index) => ({
                position: index,
                name: product?.productName,
                productId: product?.productId,
                productBatchId: '',
                quantity: product?.quantity,
                expirationDate: product?.defaultDaysToExpiration ? moment(form?.orderDate).add(product?.defaultDaysToExpiration - 1, 'days') : '',
                price: product?.purchaseNetPrice ? Number(product.purchaseNetPrice).toFixed(2) : '',
                type: product?.type
            }));
        });

        formik.setFieldValue('destinationsList', destinationsList);
        formik.setFieldValue('productsList', productsList);
    };

    const handleSubmit = (values) => {
        const locationProducts = values.destinationsList
            .map((destination, index) => ({
                podId: destination.destinationPodId,
                products: (values.productsList?.[index] ?? []).map(product => ({
                    productId: product?.productId,
                    quantity: product?.quantity,
                    price: product?.price,
                    expirationDate: product?.expirationDate
                        ? product.expirationDate.format(MISC_DATE_ISO_DATE_ONLY_FORMAT)
                        : undefined,
                })),
            }))
            .filter(location => location.products.length > 0);

        setErrors({});
        setData({
            form: {
                ...form,
                orderDate: form?.orderDate?.format(MISC_DATE_ISO_FORMAT),
                locationProducts,
                attachments: values?.attachments || [],
                orderNote: values?.orderNote
            },
            formik: values
        });
        setView(3);
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.destinationsList || values.destinationsList.length <= 1) {
            errors.destinationsList = 'Wybierz minimum 1 dodatkową lokalizację';
        }

        if (values.destinationsList && values.destinationsList.length > 0) {
            values.destinationsList.forEach((destination, index) => {
                if (destination.name === 'Magazyn główny') return;

                if (!values.productsList[index] || values.productsList[index].length === 0) {
                    productsListErrors[index] = 'Wybierz minimum 1 produkt';
                    hasErrors = true;
                } else {
                    const itemErrorsArray = [];
                    values.productsList[index].forEach((product, productIndex) => {
                        const itemErrors = {};

                        if (!product?.quantity) {
                            itemErrors.quantity = 'Pole wymagane';
                        } else if (product?.quantity <= 0) {
                            itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                        }

                        if (product?.price && product?.price <= 0) {
                            itemErrors.price = 'Proszę podać wartość dodatnią';
                        }

                        if (!product?.expirationDate) {
                            itemErrors.expirationDate = 'Pole wymagane';
                        }

                        if (Object.keys(itemErrors).length > 0) {
                            itemErrorsArray[productIndex] = itemErrors;
                            hasErrors = true;
                        }
                    });

                    if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                        productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                    }
                }
            });
        }

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    return (
        <Formik
            initialValues={ initialValues() }
            initialErrors={ initialErrors }
            validate={ validate }
            onSubmit={ handleSubmit }
        >
            <FormBody
                form={ form }
                onChangeSetDate={ onChangeSetDate }
                onCopyHistoricalOrder={ onCopyHistoricalOrder }
                onReturn={ onReturn }
            />
        </Formik>
    );
};

export default FormWithLocationDivision;